import apiService from '@/services/apiService.js'
const STORAGE_KEY = 'pos_operational_config_by_sucursal_v1'

const DEFAULT_CONFIG = {
  modo_operacion_sucursal: 'MULTIDEPOSITO_MANUAL',
  deposito_mostrador_id: null,
  deposito_default_entrada: null,
  deposito_default_recepcion: null,
  auto_transferir_a_mostrador: true,
  consumo_venta_solo_mostrador: true,
  modo_cajero_unico: false,
  atajos_teclado_habilitados: true,
  mostrar_hints_atajos: true
}

function safeParse(value, fallback) {
  try {
    return value ? JSON.parse(value) : fallback
  } catch (_) {
    return fallback
  }
}

function getCurrentSucursalId() {
  const envVal = process.env.VUE_APP_SUCURSAL_ID
  if (envVal && !Number.isNaN(Number(envVal))) return Number(envVal)

  const ls = localStorage.getItem('sucursal_id') || localStorage.getItem('sucursalId')
  if (ls && !Number.isNaN(Number(ls))) return Number(ls)

  return 1
}

function getStorage() {
  return safeParse(localStorage.getItem(STORAGE_KEY), {})
}

function persistStorage(data) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
}

function normalizeBoolean(value, fallback) {
  return value === undefined || value === null ? fallback : Boolean(value)
}

function normalizeConfig(config = {}) {
  return {
    ...DEFAULT_CONFIG,
    ...config,
    deposito_mostrador_id: config?.deposito_mostrador_id != null ? Number(config.deposito_mostrador_id) : null,
    deposito_default_entrada: config?.deposito_default_entrada != null ? Number(config.deposito_default_entrada) : null,
    deposito_default_recepcion: config?.deposito_default_recepcion != null ? Number(config.deposito_default_recepcion) : null,
    auto_transferir_a_mostrador: config?.auto_transferir_a_mostrador !== false,
    consumo_venta_solo_mostrador: config?.consumo_venta_solo_mostrador !== false,
    modo_cajero_unico: normalizeBoolean(config?.modo_cajero_unico, false),
    atajos_teclado_habilitados: normalizeBoolean(config?.atajos_teclado_habilitados, true),
    mostrar_hints_atajos: normalizeBoolean(config?.mostrar_hints_atajos, true)
  }
}

function getConfig(sucursalId = getCurrentSucursalId()) {
  const storage = getStorage()
  return normalizeConfig(storage[String(sucursalId)] || {})
}

// Las seis claves que el BACKEND persiste en `config_parametros_sistema`
// (`CONFIG_KEYS` de `operationalConfigService.js` del API). El resto de lo que
// guarda esta pantalla —modo cajero único, atajos de teclado, impresión— es
// preferencia del puesto y vive bien en el navegador.
const CLAVES_DEL_SERVIDOR = [
  'modo_operacion_sucursal',
  'deposito_mostrador_id',
  'deposito_default_entrada',
  'deposito_default_recepcion',
  'auto_transferir_a_mostrador',
  'consumo_venta_solo_mostrador'
]

function soloDelServidor(config) {
  return Object.fromEntries(
    CLAVES_DEL_SERVIDOR.filter((k) => k in config).map((k) => [k, config[k]])
  )
}

function saveConfig(sucursalId, payload) {
  const key = String(sucursalId || getCurrentSucursalId())
  const storage = getStorage()
  const nextConfig = normalizeConfig({
    ...(storage[key] || {}),
    ...payload
  })
  storage[key] = nextConfig
  persistStorage(storage)
  return nextConfig
}

// **El depósito mostrador tiene que llegar al BACKEND, no al navegador.**
//
// Encontrado el 2026-09-08: `saveConfig` escribía sólo en `localStorage` y la
// pantalla decía «Configuración operativa guardada». El backend nunca se
// enteraba, así que `getOperationalContext` seguía sin encontrar mostrador y
// **vender un producto fallaba siempre**, hiciera lo que hiciera el operador.
// El endpoint existía desde antes y nadie lo llamaba.
//
// Es el peor tipo de fallo: la pantalla afirma haber guardado. El operador no
// tiene motivo para dudar y busca el problema en cualquier otro lado.
async function saveConfigRemoto(sucursalId, payload) {
  const key = String(sucursalId || getCurrentSucursalId())
  const { data } = await apiService.put(
    `/api/inventory/operational-config/${key}`, soloDelServidor(normalizeConfig(payload))
  )
  // El backend devuelve el contexto ya validado —puede rechazar, por ejemplo,
  // un mostrador que no pertenece a la sucursal— así que lo que se guarda en
  // el navegador es lo que el servidor aceptó, no lo que se le pidió.
  const aceptado = data?.data?.config || data?.config || null
  return saveConfig(key, aceptado ? { ...payload, ...aceptado } : payload)
}

function getOperationalSummary({ sucursalId = getCurrentSucursalId(), warehouses = [], config: explicitConfig = null } = {}) {
  const config = normalizeConfig(explicitConfig || getConfig(sucursalId))
  const activeEnabled = (warehouses || []).filter(dep => dep.activo && dep.habilitado)
  const counterWarehouse = activeEnabled.find(dep => dep.id === config.deposito_mostrador_id) || null
  const hasSingleOperationalWarehouse = activeEnabled.length === 1
  const singleOperationalWarehouse = hasSingleOperationalWarehouse ? activeEnabled[0] : null
  const canAutoUseCounter = Boolean(
    config.modo_operacion_sucursal === 'MONODEPOSITO_AUTO_MOSTRADOR' &&
    hasSingleOperationalWarehouse &&
    counterWarehouse &&
    counterWarehouse.id === singleOperationalWarehouse.id
  )

  return {
    sucursalId,
    config,
    activeEnabled,
    counterWarehouse,
    hasSingleOperationalWarehouse,
    singleOperationalWarehouse,
    canAutoUseCounter
  }
}

function resolveDestinationWarehouse({
  purpose = 'entrada',
  sucursalId = getCurrentSucursalId(),
  warehouses = [],
  config: explicitConfig = null
} = {}) {
  const summary = getOperationalSummary({ sucursalId, warehouses, config: explicitConfig })
  const { config, counterWarehouse, canAutoUseCounter } = summary

  if (canAutoUseCounter && counterWarehouse) {
    return {
      depositoId: counterWarehouse.id,
      autoResolved: true,
      requiresSelection: false,
      reason: 'mostrador_unico'
    }
  }

  const defaultWarehouseId = purpose === 'recepcion'
    ? config.deposito_default_recepcion
    : config.deposito_default_entrada
  const defaultWarehouse = summary.activeEnabled.find(dep => dep.id === defaultWarehouseId) || null

  if (config.modo_operacion_sucursal === 'MULTIDEPOSITO_DEPOSITO_DEFAULT' && defaultWarehouse) {
    return {
      depositoId: defaultWarehouse.id,
      autoResolved: true,
      requiresSelection: false,
      reason: purpose === 'recepcion' ? 'default_recepcion' : 'default_entrada'
    }
  }

  return {
    depositoId: null,
    autoResolved: false,
    requiresSelection: true,
    reason: 'seleccion_manual'
  }
}

function isAutoTransferMovement(movement) {
  const motivo = String(movement?.motivo || '').toLowerCase()
  return motivo.includes('auto-transferencia') || motivo.includes('autoabastecimiento')
}

export default {
  DEFAULT_CONFIG,
  getCurrentSucursalId,
  getConfig,
  saveConfig,
  saveConfigRemoto,
  getOperationalSummary,
  resolveDestinationWarehouse,
  isAutoTransferMovement
}
